import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';

import OnboardingShell, { PlanFeatures, useOnboardingStyles } from '../OnboardingShell';
import SectionEyebrow from '../../../MyComponents/LandingSections/SectionEyebrow';
import SpinnerContentRenderer from '../../../MyComponents/Drawer/SpinnerContentRenderer';
import managementApi from '../../../Api/managementApi';
import { SUBSCRIPTIONS_LINK } from '../../../common/urlLinks';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';

// http://localhost:3000/onboarding?step=START&subscription=<offer-key>

const useStyles = makeStyles(() => ({
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: '22px',
    },
    head: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '10px',
        marginBottom: '12px',
    },
    text: {
        margin: '0 0 16px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.98rem',
        lineHeight: 1.6,
    },
    value: {
        margin: '20px 0 16px',
        fontSize: '1.6rem',
        letterSpacing: '-0.03em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    wide: {
        width: '100%',
    },
}));

/**
 * A named special offer (a partner or school deal): the offer's own text,
 * then one card per plan it contains, with checkout for signed-in visitors
 * and "create account" otherwise.
 */
const SpecialStage = ({ state, subscriptions, language, isLoggedIn }) => {
    const classes = useOnboardingStyles();
    const own = useStyles();
    const [specialOffers, setSpecialOffer] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        managementApi
            .getSpecialOffers()
            .then((res) => {
                setSpecialOffer(res.data);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                console.error('Unable to get special data', err);
            });
    }, []);

    if (loading) {
        return (
            <div style={{ width: '100%', textAlign: 'center', padding: '4rem 0' }}>
                <SpinnerContentRenderer />
            </div>
        );
    }

    const offer = specialOffers && state.subscription && specialOffers[state.subscription.toLowerCase()];
    if (!offer) {
        return (
            <OnboardingShell
                eyebrowId="redesign.onboarding.special.eyebrow"
                title={<FormattedMessage id="subscription.upsell.dialog.title" />}
            >
                <div className={classes.actions} style={{ marginTop: 0 }}>
                    <Link to={SUBSCRIPTIONS_LINK} className={classes.primary}>
                        <FormattedMessage id="redesign.cta.plans" />
                        <span aria-hidden="true">→</span>
                    </Link>
                </div>
            </OnboardingShell>
        );
    }

    const lang = language.toUpperCase();

    return (
        <OnboardingShell
            eyebrowId="redesign.onboarding.special.eyebrow"
            title={<span dangerouslySetInnerHTML={{ __html: offer.text[lang] }} />}
        >
            <div className={own.grid}>
                {offer.offers.map((el) => (
                    <article key={`offers-price-${el.id}`} className={classes.card}>
                        <div className={own.head}>
                            <SectionEyebrow variant="card">
                                <FormattedMessage id={`subscriptions.subscription.${el.type}.name`} />
                            </SectionEyebrow>
                        </div>
                        <p className={own.text}>{el.text[lang]}</p>
                        <PlanFeatures type={el.type} subscriptions={subscriptions} />
                        <p className={own.value}>{el.value}</p>
                        {isLoggedIn ? (
                            <form action={`/api/private/profile/specialCheckout?priceId=${el.id}`} method="POST">
                                <button type="submit" className={`${classes.primary} ${own.wide}`}>
                                    <FormattedMessage id="subscriptions.subscription.checkout" />
                                    <span aria-hidden="true">→</span>
                                </button>
                            </form>
                        ) : (
                            <button
                                type="button"
                                className={`${classes.primary} ${own.wide}`}
                                onClick={() => {
                                    window.localStorage.setItem(
                                        'pathBeforeLogin',
                                        window.location.pathname + window.location.search
                                    );
                                    window.openAuthentication();
                                }}
                            >
                                <FormattedMessage id="createAccount" />
                                <span aria-hidden="true">→</span>
                            </button>
                        )}
                    </article>
                ))}
            </div>
        </OnboardingShell>
    );
};

const mapStateToProps = (state) => {
    const { mainAppState } = state;
    return { subscriptions: mainAppState.subscriptions };
};

export default connect(mapStateToProps)(SpecialStage);
